/*
 * Copyright (c) 2020, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: MIT
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/MIT
 */
import { getOwnPropertyDescriptor, ArrayFilter, MapCreate } from '../shared/utils';

const eventTargetGetter: (this: Event) => EventTarget = getOwnPropertyDescriptor(Event.prototype, 'target')!.get!;

const focusEventRelatedTargetGetter: (this: FocusEvent) => EventTarget | null = getOwnPropertyDescriptor(
  FocusEvent.prototype,
  'relatedTarget'
)!.get!;

const { composedPath } = Event.prototype;

interface EventMeta {
  target: EventTarget | null;
  composedPath: EventTarget[];
  relatedTarget?: EventTarget | null;
}

const eventsMeta: WeakMap<Event, EventMeta> = new WeakMap();

export function extractEventMetadata(event: Event): EventMeta {
  let meta: EventMeta | undefined = eventsMeta.get(event);
  if (meta) {
    return meta;
  }
  meta = {
    target: eventTargetGetter.call(event),
    composedPath: composedPath.call(event),
  };
  if (event instanceof FocusEvent) {
    meta.relatedTarget = focusEventRelatedTargetGetter.call(event);
  }
  eventsMeta.set(event, meta);
  return meta;
}

const targetDistortion = function target(this: Event): EventTarget | null {
  return extractEventMetadata(this).target;
};

const composedPathDistortion = function composedPath(this: Event): EventTarget[] {
  return ArrayFilter.call(
    extractEventMetadata(this).composedPath,
    (et: EventTarget): boolean => !(et instanceof ShadowRoot)
  );
};

const relatedTargetDistortion = function relatedTarget(this: Event): EventTarget | null | undefined {
  return extractEventMetadata(this).relatedTarget;
};

export default MapCreate([
  [eventTargetGetter, targetDistortion],
  [composedPath, composedPathDistortion],
  [focusEventRelatedTargetGetter, relatedTargetDistortion],
]);
